'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface ServiceSectionProps {
  className?: string;
}

const ServiceSection = ({ className = '' }: ServiceSectionProps) => {
  const services = [
    {
      id: 1,
      icon: "AcademicCapIcon",
      title: "University Selection",
      description: "Expert guidance to choose the perfect university that matches your academic goals and career aspirations.",
      features: ["Personalized matching", "Program comparison", "Admission requirements"]
    },
    {
      id: 2,
      icon: "DocumentTextIcon",
      title: "Application Assistance",
      description: "Comprehensive support throughout your application process from document preparation to submission.",
      features: ["Document review", "Essay editing", "Application tracking"]
    },
    {
      id: 3,
      icon: "CurrencyDollarIcon",
      title: "Visa & Financial Aid",
      description: "Navigate visa processes and explore scholarship opportunities to make your education affordable.",
      features: ["Visa guidance", "Scholarship search", "Financial planning"]
    },
    {
      id: 4,
      icon: "HomeIcon",
      title: "Pre-Departure Support",
      description: "Get ready for your journey with accommodation assistance, travel planning, and cultural orientation.",
      features: ["Accommodation help", "Travel booking", "Cultural briefing"]
    },
    {
      id: 5,
      icon: "UserGroupIcon",
      title: "Career Counseling",
      description: "Professional guidance to align your education with career goals and maximize future opportunities.",
      features: ["Career assessment", "Industry insights", "Job market trends"]
    },
    {
      id: 6,
      icon: "LanguageIcon",
      title: "Test Preparation",
      description: "Comprehensive coaching for IELTS, TOEFL, GRE, GMAT and other standardized tests.",
      features: ["Expert tutoring", "Practice tests", "Score improvement"]
    }
  ];

  return (
    <section className={`py-16 md:py-24 bg-muted ${className}`}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Our Services
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Comprehensive support at every step of your study abroad journey. We're with you from start to success.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-card rounded-xl p-6 shadow-card hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-2 border border-border"
            >
              <div className="w-14 h-14 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name={service.icon} size={28} className="text-primary" />
              </div>
              
              <h3 className="text-xl font-bold text-foreground mb-3">{service.title}</h3>
              <p className="text-muted-foreground mb-4">{service.description}</p>
              
              <ul className="space-y-2 mb-6">
                {service.features.map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2 text-sm text-foreground">
                    <Icon name="CheckCircleIcon" size={16} className="text-secondary" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

               <Link
                 //href="/services-overview"
                 href="/consultation-booking"
                 className="inline-flex items-center space-x-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors duration-300"
               >
                <span>Learn More...</span>
                <Icon name="ArrowRightIcon" size={16} className="text-current" />
              </Link>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            //href="/services-overview"
            href="/consultation-booking" 
            className="inline-flex items-center space-x-2 px-8 py-4 bg-secondary text-secondary-foreground font-semibold rounded-lg hover:bg-secondary/90 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
          >
            <span>Book Consultation</span>
            <Icon name="ArrowRightIcon" size={20} className="text-current" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServiceSection;